/**
 * UNIFIED ANIMATION PRESETS
 * Global Framer Motion configurations for consistent animations across SAS-Sindh
 * 
 * Usage:
 * import { animationPresets } from '@/utils/animationPresets';
 * <motion.div {...animationPresets.slideUpFadeIn}>Content</motion.div>
 */

export const animationPresets = {
  /**
   * ENTRANCE ANIMATIONS
   */

  // Slide up + Fade in (Universal page & card entrance)
  slideUpFadeIn: {
    initial: { y: 20, opacity: 0 },
    animate: { y: 0, opacity: 1 },
    exit: { y: -20, opacity: 0 },
    transition: { 
      duration: 0.5, 
      ease: [0.34, 1.56, 0.64, 1] // Spring curve
    }
  },

  // Subtle fade in (Overlay, modals)
  fadeIn: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.3, ease: 'easeInOut' }
  },

  // Scale + Fade (Card entrance with emphasis)
  scaleUpFadeIn: {
    initial: { scale: 0.92, opacity: 0 },
    animate: { scale: 1, opacity: 1 },
    exit: { scale: 0.92, opacity: 0 },
    transition: { 
      duration: 0.4, 
      ease: [0.34, 1.56, 0.64, 1]
    }
  },

  // Slide from left (Sidebar, drawer)
  slideInFromLeft: {
    initial: { x: -100, opacity: 0 },
    animate: { x: 0, opacity: 1 },
    exit: { x: -100, opacity: 0 },
    transition: { 
      duration: 0.35, 
      ease: [0.34, 1.56, 0.64, 1]
    }
  },

  // Slide from right (Modals, panels)
  slideInFromRight: {
    initial: { x: 100, opacity: 0 },
    animate: { x: 0, opacity: 1 },
    exit: { x: 100, opacity: 0 },
    transition: { 
      duration: 0.35, 
      ease: [0.34, 1.56, 0.64, 1]
    }
  },

  // Stagger list items (Multiple cards)
  staggerContainer: {
    initial: 'hidden',
    animate: 'visible',
    exit: 'hidden',
    variants: {
      visible: {
        transition: {
          staggerChildren: 0.08,
          delayChildren: 0.1,
          ease: 'easeInOut'
        }
      },
      hidden: {
        transition: {
          staggerChildren: 0.05,
          staggerDirection: -1
        }
      }
    }
  },

  staggerItem: {
    variants: {
      hidden: { y: 20, opacity: 0 },
      visible: {
        y: 0,
        opacity: 1,
        transition: { duration: 0.4, ease: [0.34, 1.56, 0.64, 1] }
      }
    }
  },

  /**
   * HOVER ANIMATIONS
   */

  hoverLift: {
    whileHover: { 
      y: -4,
      transition: { duration: 0.2, ease: 'easeOut' }
    },
    whileTap: { y: 0 }
  },

  hoverScale: {
    whileHover: { 
      scale: 1.04,
      transition: { duration: 0.2, ease: 'easeOut' }
    },
    whileTap: { scale: 0.98 }
  },

  hoverGlow: {
    whileHover: {
      boxShadow: '0 20px 40px -5px rgba(0, 0, 0, 0.12), inset 0 0 0 1px rgba(255, 255, 255, 0.5)',
      transition: { duration: 0.2, ease: 'easeOut' }
    }
  },

  /**
   * TAP/CLICK ANIMATIONS
   */

  tapScale: {
    whileTap: { scale: 0.96 },
    transition: { duration: 0.15, ease: 'easeInOut' }
  },

  /**
   * VIEW-TRIGGERED ANIMATIONS (Scroll into view)
   */

  slideUpOnView: {
    initial: { y: 40, opacity: 0 },
    whileInView: { y: 0, opacity: 1 },
    viewport: { once: true, amount: 0.3 },
    transition: { duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }
  },

  scaleUpOnView: {
    initial: { scale: 0.85, opacity: 0 },
    whileInView: { scale: 1, opacity: 1 },
    viewport: { once: true, amount: 0.3 },
    transition: { duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }
  },

  /**
   * PAGE TRANSITIONS
   */

  pageEnter: {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -10 },
    transition: { duration: 0.4, ease: 'easeInOut' }
  },

  pageExit: {
    exit: { opacity: 0, y: 10 },
    transition: { duration: 0.3, ease: 'easeInOut' }
  },

  /**
   * CONTINUOUS ANIMATIONS
   */

  floatingAnimation: {
    animate: {
      y: [0, -8, 0],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: 'easeInOut'
      }
    }
  },

  pulseAnimation: {
    animate: {
      opacity: [1, 0.6, 1],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: 'easeInOut'
      }
    }
  },

  rotateAnimation: {
    animate: {
      rotate: 360,
      transition: {
        duration: 8,
        repeat: Infinity,
        ease: 'linear'
      }
    }
  },

  /**
   * LOADING ANIMATIONS
   */

  shimmerAnimation: {
    initial: { backgroundPosition: '200% center' },
    animate: { backgroundPosition: '-200% center' },
    transition: { duration: 1.5, repeat: Infinity }
  },

  /**
   * LAYOUT ANIMATIONS (AnimatePresence context)
   */

  layoutAnimation: {
    layout: true,
    transition: { duration: 0.3, ease: 'easeInOut' }
  }
};

/**
 * HELPER: Preset with custom delay
 * Usage: {animationPresets.slideUpFadeIn, transition: { ...animationPresets.slideUpFadeIn.transition, delay: 0.1 }}
 */
export const withDelay = (preset, delay) => ({
  ...preset,
  transition: {
    ...preset.transition,
    delay
  }
});

/**
 * HELPER: Combine multiple preset effects
 * Usage: combinePresets([animationPresets.slideUpFadeIn, animationPresets.hoverLift])
 */
export const combinePresets = (presets) => {
  const combined = {};
  
  presets.forEach(preset => {
    Object.keys(preset).forEach(key => {
      if (key === 'transition') {
        combined.transition = { ...combined.transition, ...preset[key] };
      } else {
        combined[key] = { ...combined[key], ...preset[key] };
      }
    });
  });

  return combined;
};

export default animationPresets;
